import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Info, TrendingDown, TrendingUp } from 'lucide-react';
import { UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import ReportWidget from './components/ReportWidget';
import MixedCurrencyBanner from './components/MixedCurrencyBanner';
import PerOrgCostTile from './components/PerOrgCostTile';
import { fetchDowntimeCost } from '../../store/reporting/actions';
import { formatDateTimeForAPI } from '../../constants/utils';
import { formatCurrency as formatCurrencyBase } from '../../helpers/formatCurrency';

const COLOR = '#f59e0b';
const EM_DASH = '\u2014';

// Summary widgets render without decimals; the shared helper handles locale + symbol.
const formatCurrency = (value, currency) =>
  formatCurrencyBase(value, currency, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const TrendBadge = ({ value, label }) => {
  const { t } = useTranslation();
  if (value === null || value === undefined) {
    return (
      <div className="text-center">
        <h3 className="mb-2 fw-bold text-muted" style={{ fontSize: '1.4rem' }}>{EM_DASH}</h3>
        <span className="text-muted" style={{ fontSize: '0.8rem' }}>{label}</span>
      </div>
    );
  }
  const up = value > 0;
  const flat = value === 0;
  const color = flat ? '#6b7280' : (up ? '#ef4444' : '#10b981');
  const Icon = flat ? null : (up ? TrendingUp : TrendingDown);
  const sign = up ? '+' : '';
  return (
    <div className="text-center" title={t('reporting.downtime_cost.trend_hint', 'Cost change vs. the previous equal-length period')}>
      <h3 className="mb-2 fw-bold d-inline-flex align-items-center gap-1" style={{ fontSize: '1.4rem', color }}>
        {Icon && <Icon size={18} />}
        <span>{sign}{value.toFixed(1)}%</span>
      </h3>
      <div>
        <span className="text-muted" style={{ fontSize: '0.8rem' }}>{label}</span>
      </div>
    </div>
  );
};

const CostTooltip = ({ active, payload, currency }) => {
  const { t } = useTranslation();
  if (!active || !payload || !payload.length) return null;
  const dataPoint = payload[0]?.payload;
  if (!dataPoint) return null;
  return (
    <div style={{
      backgroundColor: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      padding: 12,
      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
      fontSize: '0.85rem',
    }}>
      <div className="fw-bold mb-2">{dataPoint.periodLabel}</div>
      <div className="d-flex align-items-center gap-2">
        <span style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: COLOR, display: 'inline-block' }} />
        <span className="text-muted">{t('reporting.downtime_cost.cost', 'Cost')}</span>
        <span className="fw-bold ms-auto">{formatCurrency(dataPoint.cost, currency)}</span>
      </div>
    </div>
  );
};

/**
 * Self-fetching by default. When {@code data} is passed externally the
 * widget skips its Redux dispatch and renders the supplied payload —
 * used by the Global Dashboard to embed the same JSX cross-org.
 */
const DowntimeCostWidget = ({
  organizationId, dateRange, criticality, locationId, vendorId, assetId, assetTypeId, contractNumber,
  data: externalData, loading: externalLoading, error: externalError,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    downtimeCost: reduxData,
    downtimeCostLoading: reduxLoading,
    downtimeCostError: reduxError,
  } = useSelector(state => state.Reporting);
  const externalMode = externalData !== undefined;
  const data = externalMode ? externalData : reduxData;
  const loading = externalMode ? !!externalLoading : reduxLoading;
  const error = externalMode ? externalError : reduxError;

  useEffect(() => {
    if (externalMode) return;
    if (!organizationId || !dateRange?.startDate || !dateRange?.endDate) return;
    dispatch(fetchDowntimeCost(
      organizationId,
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      criticality,
      locationId,
      vendorId,
      assetId,
      assetTypeId,
      contractNumber,
    ));
  }, [externalMode, dispatch, organizationId, dateRange?.startDate, dateRange?.endDate, criticality, locationId, vendorId, assetId, assetTypeId, contractNumber]);

  const handleRefresh = useCallback(() => {
    if (externalMode) return;
    if (!organizationId || !dateRange?.startDate || !dateRange?.endDate) return;
    dispatch(fetchDowntimeCost(
      organizationId,
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      criticality,
      locationId,
      vendorId,
      assetId,
      assetTypeId,
      contractNumber,
    ));
  }, [externalMode, dispatch, organizationId, dateRange, criticality, locationId, vendorId, assetId, assetTypeId, contractNumber]);

  const summary = data?.summary;
  const chartData = data?.chartData || [];
  // Mixed-currency mode is set by the backend on Global View when sub-orgs use different
  // currencies. Aggregate fields are null in that case; the perOrg tiles are the source of truth.
  const isMixedCurrency = !!summary?.mixedCurrency;
  const perOrgBreakdown = summary?.perOrg || [];
  // "All missing estimates" means every in-scope asset lacks avg price or duration
  const allMissing = summary && summary.assetsInScope > 0 &&
    summary.assetsInScope === summary.assetsWithoutEstimates;
  const hasCost = summary && !allMissing && summary.currentTotalCost !== null && summary.currentTotalCost !== undefined;
  const currency = summary?.currency;
  const isEmpty = !data || (
    isMixedCurrency
      ? perOrgBreakdown.length === 0
      : (!chartData.length && !hasCost)
  );

  const emptyMessage = allMissing
    ? t('reporting.downtime_cost.empty_no_estimates', 'No assets in scope have an average price or duration set. Add them on the asset form to see downtime cost.')
    : t('reporting.downtime_cost.empty', 'No downtime cost to display for the selected period.');

  return (
    <ReportWidget
      title={
        <span className="d-flex align-items-center gap-2">
          {t('reporting.downtime_cost.title', 'Downtime Cost')}
          <Info size={15} className="text-muted" id="downtimeCostTooltip" style={{ cursor: 'help' }} />
          <UncontrolledTooltip placement="top" target="downtimeCostTooltip">
            {t('reporting.downtime_cost.tooltip', 'Estimated downtime cost per asset, calculated from (downtime hours × average price per case ÷ average duration per case). Assets missing either estimate are excluded.')}
          </UncontrolledTooltip>
        </span>
      }
      loading={loading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage={emptyMessage}
      onRefresh={externalMode ? undefined : handleRefresh}
    >
      {isMixedCurrency ? (
        <div>
          <MixedCurrencyBanner />
          <div
            className="d-flex flex-row flex-nowrap gap-3 pb-2"
            style={{ overflowX: 'auto' }}
          >
            {perOrgBreakdown.map((row) => (
              <PerOrgCostTile
                key={row.organizationId}
                organizationName={row.organizationName}
                currency={row.currency}
                currentTotalCost={row.currentTotalCost}
                variationPercent={row.variationPercent}
              />
            ))}
          </div>
        </div>
      ) : (
      <>
      {summary && (
        <div className="d-flex justify-content-center gap-5 mb-4 py-2">
          <div className="text-center">
            <h3 className="mb-2 fw-bold" style={{ fontSize: '1.75rem' }}>
              {hasCost ? formatCurrency(summary.currentTotalCost, currency) : EM_DASH}
            </h3>
            <span
              className="px-3 py-1 rounded-pill d-inline-block text-center"
              style={{ border: `2px solid ${COLOR}`, color: COLOR, fontSize: '0.8rem', fontWeight: 500, minWidth: 90 }}
            >
              {t('reporting.downtime_cost.current_total', 'Current Total')}
            </span>
          </div>
          {/* WoW / MoM trend badges — hidden for now. Uncomment to restore.
          <TrendBadge value={hasCost ? summary.weekOverWeekVariation : null} label={t('reporting.downtime_cost.wow', 'Week-over-Week')} />
          <TrendBadge value={hasCost ? summary.monthOverMonthVariation : null} label={t('reporting.downtime_cost.mom', 'Month-over-Month')} />
          */}
        </div>
      )}

      {summary && summary.assetsWithoutEstimates > 0 && !allMissing && (
        <div className="text-center text-muted mb-3" style={{ fontSize: '0.8rem' }}>
          {t('reporting.downtime_cost.partial_estimates', '{{missing}} of {{total}} assets missing avg price or duration — excluded from totals.', {
            missing: summary.assetsWithoutEstimates,
            total: summary.assetsInScope,
          })}
        </div>
      )}

      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="periodLabel" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => formatCurrency(v, currency)} width={80} />
          <Tooltip content={<CostTooltip currency={currency} />} />
          <Area
            type="monotone"
            dataKey="cost"
            stroke={COLOR}
            fill={COLOR}
            fillOpacity={0.35}
            name={t('reporting.downtime_cost.cost', 'Cost')}
          />
        </AreaChart>
      </ResponsiveContainer>
      </>
      )}
    </ReportWidget>
  );
};

export default DowntimeCostWidget;
